// PRC-029: plantilla de página de lectura (Términos, Privacidad, Metodología)
// dentro de la cabecera y el pie de la portada (PublicLayout).
import React, { useEffect, useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { J } from '../journeyTokens';

export interface DocSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface DocPageProps {
  eyebrow: string;
  title: string;
  updated: string;
  intro?: React.ReactNode;
  sections: DocSection[];
}

// Altura de la cabecera fija de la portada: los títulos no quedan debajo.
const HEADER_OFFSET = 96;

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  window.history.replaceState(window.history.state, '', `#${id}`);
};

// --- Piezas de texto del documento ---

export const DocP: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="p" sx={{ m: 0, mb: 2, color: J.body, fontSize: 16, lineHeight: 1.75 }}>{children}</Box>
);

export const DocH3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="h3" sx={{ m: 0, mt: 3.5, mb: 1.25, color: J.text, fontSize: 17, fontWeight: 700, lineHeight: 1.4 }}>{children}</Box>
);

export const DocList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box component="ul" sx={{ m: 0, mb: 2, pl: '22px', color: J.body, fontSize: 16, lineHeight: 1.75, '& li': { mb: 0.75 }, '& li::marker': { color: J.sky } }}>
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </Box>
);

export const DocNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ my: 2.5, p: 2, borderRadius: '12px', border: `1px solid ${J.border}`, bgcolor: 'rgba(15,23,42,.6)', color: J.body, fontSize: 15, lineHeight: 1.7 }}>{children}</Box>
);

export const DocCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="pre" sx={{ my: 2.5, p: 2, borderRadius: '12px', border: `1px solid ${J.border}`, bgcolor: '#020617', color: J.skyText, fontSize: 13.5, lineHeight: 1.7, overflowX: 'auto', fontFamily: '"JetBrains Mono", ui-monospace, monospace', whiteSpace: 'pre' }}>{children}</Box>
);

const DocPage: React.FC<DocPageProps> = ({ eyebrow, title, updated, intro, sections }) => {
  const [active, setActive] = useState(sections[0]?.id);

  // Marca en el índice la sección visible.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: `-${HEADER_OFFSET}px 0px -55% 0px` },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  // Abrir con #sección en la dirección lleva a ella.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) window.setTimeout(() => document.getElementById(hash)?.scrollIntoView({ block: 'start' }), 0);
  }, []);

  const toc = (
    <Box component="nav" aria-label="Índice" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Box sx={{ color: J.muted, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', mb: 1 }}>En esta página</Box>
      {sections.map((s) => (
        <Box
          key={s.id}
          component="a"
          href={`#${s.id}`}
          aria-current={active === s.id ? 'location' : undefined}
          onClick={(e: React.MouseEvent) => { e.preventDefault(); scrollToId(s.id); }}
          sx={{
            py: '6px', pl: '12px', borderLeft: `2px solid ${active === s.id ? J.sky : J.border}`,
            color: active === s.id ? J.text : J.muted, fontSize: 13.5, fontWeight: active === s.id ? 700 : 500,
            textDecoration: 'none', lineHeight: 1.4, transition: 'color .18s ease, border-color .18s ease',
            '&:hover': { color: J.text },
            '&:focus-visible': { outline: `2px solid ${J.sky}`, outlineOffset: 2, borderRadius: '4px' },
          }}
        >
          {s.title}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', bgcolor: J.bg, color: J.text, fontFamily: J.font, pt: { xs: 5, md: 8 }, pb: { xs: 8, md: 12 } }}>
      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, md: 3 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 720px)' }, columnGap: { md: 8 }, justifyContent: 'center' }}>
        {/* Cabecera del documento */}
        <Box sx={{ gridColumn: { md: '2' }, mb: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', color: J.amber, fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            <Box component="span" aria-hidden sx={{ width: 28, height: '1px', bgcolor: J.amber }} />
            {eyebrow}
          </Box>
          <Box component="h1" sx={{ m: 0, mt: 2, fontSize: { xs: 34, md: 44 }, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 }}>{title}</Box>
          <Box sx={{ mt: 1.5, color: J.muted, fontSize: 14 }}>Última actualización: {updated}</Box>
          {intro && <Box sx={{ mt: 3, color: J.body, fontSize: 17, lineHeight: 1.7 }}>{intro}</Box>}
        </Box>

        {/* Índice: lateral fijo en escritorio, al principio en el móvil */}
        <Box sx={{ gridColumn: { md: '1' }, gridRow: { md: '2' }, mb: { xs: 4, md: 0 } }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: HEADER_OFFSET + 16 }, p: { xs: 2, md: 0 }, borderRadius: { xs: '12px', md: 0 }, border: { xs: `1px solid ${J.border}`, md: 'none' } }}>
            {toc}
          </Box>
        </Box>

        {/* Columna de lectura */}
        <Box component="article" sx={{ gridColumn: { md: '2' }, gridRow: { md: '2' }, minWidth: 0 }}>
          {sections.map((s, index) => (
            <Box key={s.id} component="section" id={s.id} aria-labelledby={`${s.id}-title`} sx={{ scrollMarginTop: `${HEADER_OFFSET}px`, pt: index === 0 ? 0 : 5, mt: index === 0 ? 0 : 5, borderTop: index === 0 ? 'none' : `1px solid ${J.border}` }}>
              <Box component="h2" sx={{ m: 0, mb: 2, fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.25, display: 'flex', alignItems: 'baseline', gap: 1, '&:hover .doc-anchor': { opacity: 1 } }}>
                <span id={`${s.id}-title`}>{s.title}</span>
                <Box
                  component="a"
                  className="doc-anchor"
                  href={`#${s.id}`}
                  aria-label={`Enlace a «${s.title}»`}
                  onClick={(e: React.MouseEvent) => { e.preventDefault(); scrollToId(s.id); }}
                  sx={{ opacity: 0, color: J.muted, textDecoration: 'none', fontSize: 18, fontWeight: 500, transition: 'opacity .18s ease', '&:focus-visible': { opacity: 1 } }}
                >
                  #
                </Box>
              </Box>
              {s.content}
            </Box>
          ))}
          <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${J.border}` }}>
            <ButtonBase
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ gap: '6px', color: J.skyText, fontSize: 14, fontWeight: 700, borderRadius: '6px', px: '4px', fontFamily: J.font, '&.Mui-focusVisible': { outline: `2px solid ${J.sky}` } }}
            >
              <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} /> Volver arriba
            </ButtonBase>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DocPage;
